/**
 * Explore Countries Tab - Browse and filter countries
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { CountryFilters, CountryList } from '../../country/components';
import { countryAPI } from '../../country/api/country-api';
import { SEO, Header } from '../../../shared/components';
import { ExploreTabs } from '../components';
import type { Country, CountryFilters as CountryFiltersType, CountryFilterOptions } from '../../country/types';

const ExploreCountriesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countries, setCountries] = useState<Country[]>([]);
  const [filterOptions, setFilterOptions] = useState<CountryFilterOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
    limit: 20
  });

  // Parse filters from URL
  const getFiltersFromURL = (): CountryFiltersType => {
    return {
      region: searchParams.get('region') || undefined,
      minSafetyIndex: searchParams.get('min_safety') ? parseFloat(searchParams.get('min_safety')!) : undefined,
      maxLivingCost: searchParams.get('max_living_cost') ? parseInt(searchParams.get('max_living_cost')!) : undefined,
      postStudyWork: searchParams.get('post_study_work') === 'true',
      partTimeWork: searchParams.get('part_time_work') === 'true',
      permanentResidency: searchParams.get('permanent_residency') === 'true',
      page: searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1,
      sort: searchParams.get('sort') || 'name'
    };
  };

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await countryAPI.getFilterOptions();
        setFilterOptions(options);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    loadFilterOptions();
  }, []);

  // Load countries when filters change
  useEffect(() => {
    const loadCountries = async () => {
      setIsLoading(true);
      try {
        const filters = getFiltersFromURL();
        const result = await countryAPI.browseCountries(filters);
        setCountries(result.countries);
        setPagination(result.pagination);
      } catch (err) {
        console.error('Failed to load countries:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadCountries();
  }, [searchParams]);

  // Update URL when filters change
  const handleFilterChange = (filters: CountryFiltersType) => {
    const params: any = {};

    if (filters.region) params.region = filters.region;
    if (filters.minSafetyIndex !== undefined) params.min_safety = filters.minSafetyIndex.toString();
    if (filters.maxLivingCost !== undefined) params.max_living_cost = filters.maxLivingCost.toString();
    if (filters.postStudyWork) params.post_study_work = 'true';
    if (filters.partTimeWork) params.part_time_work = 'true';
    if (filters.permanentResidency) params.permanent_residency = 'true';
    if (filters.sort && filters.sort !== 'name') params.sort = filters.sort;
    if (filters.page && filters.page !== 1) params.page = filters.page.toString();

    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const filters = getFiltersFromURL();
    handleFilterChange({ ...filters, page });
  };

  const handleResetFilters = () => {
    setSearchParams({});
  };

  const handleCountryClick = (countryId: number) => {
    navigate(`/country/${countryId}`, {
      state: { from: `/explore/countries?${searchParams.toString()}` }
    });
  };

  if (!filterOptions) {
    return (
      <>
        <SEO title="Browse Countries" />
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex items-center gap-3">
            <Loader2 className="h-7 w-7 animate-spin text-success" />
            <span className="text-foreground font-medium">Loading countries...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Explore Countries - 10+ Study Destinations"
        description="Browse 10+ countries for international study. Filter by safety, living costs, work rights, and visa policies."
      />

      <Header />
      <ExploreTabs />

      <div className="min-h-screen bg-background">
        {/* Main Content with Layout */}
        <div className="container mx-auto px-6 py-10">
          <div className="grid grid-cols-12 gap-8">
            {/* Sticky Sidebar Filters */}
            <div className="col-span-12 lg:col-span-3">
              <div className="sticky top-24">
                <CountryFilters
                  filters={getFiltersFromURL()}
                  filterOptions={filterOptions}
                  onFilterChange={handleFilterChange}
                  onReset={handleResetFilters}
                />
              </div>
            </div>

            {/* Country Results */}
            <div className="col-span-12 lg:col-span-9">
              <CountryList
                countries={countries}
                pagination={pagination}
                isLoading={isLoading}
                onPageChange={handlePageChange}
                onCountryClick={handleCountryClick}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExploreCountriesPage;
